import { CheckboxSectionRow } from '@ui/elements/SectionRow'
import type { ComponentProps } from 'react'
import {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  useController,
  useFormContext,
} from 'react-hook-form'

type BaseFormCheckboxSectionRowProps<T extends FieldValues = FieldValues> = {
  name: Path<T>
  control: Control<T>
  rules?: RegisterOptions<T, Path<T>>
  onValueChange?: (nextValue: boolean) => void
} & Omit<ComponentProps<typeof CheckboxSectionRow>, 'value' | 'onChange' | 'status'>

type FormCheckboxSectionRowWithContextProps<T extends FieldValues = FieldValues> =
  Omit<BaseFormCheckboxSectionRowProps<T>, 'control'>

const BaseFormCheckboxSectionRow = <T extends FieldValues = FieldValues>({
  name,
  control,
  rules,
  onValueChange,
  ...props
}: BaseFormCheckboxSectionRowProps<T>) => {
  const { field, fieldState } = useController({
    name,
    control,
    rules,
  })

  const value = !!field.value
  const status = fieldState.error ? 'danger' : value ? 'success' : 'default'

  const handleChange = () => {
    const nextValue = !value
    field.onChange(nextValue)
    field.onBlur()
    onValueChange?.(nextValue)
  }

  return (
    <CheckboxSectionRow
      {...props}
      value={value}
      status={status}
      onChange={handleChange}
    />
  )
}

type FormCheckboxSectionRowProps<T extends FieldValues> = {
  control?: Control<T>
} & FormCheckboxSectionRowWithContextProps<T>

const FormCheckboxSectionRowWithContext = <T extends FieldValues = FieldValues>(
  props: FormCheckboxSectionRowWithContextProps<T>
) => {
  const { control } = useFormContext<T>()
  return <BaseFormCheckboxSectionRow {...props} control={control} />
}

const FormCheckboxSectionRow = <T extends FieldValues>(
  props: FormCheckboxSectionRowProps<T>
) => {
  const { control } = props

  if (!control) {
    return <FormCheckboxSectionRowWithContext {...props} />
  }

  return <BaseFormCheckboxSectionRow {...props} control={control} />
}

export default FormCheckboxSectionRow
